'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Quote {
  id: string;
  quoteNumber: string;
  serviceType: string;
  origin: string;
  destination: string;
  amount: number;
  status: string;
  requestDate: string;
  validUntil: string;
}

const mockQuotes: Quote[] = [
  {
    id: '1',
    quoteNumber: 'QT-2026-001',
    serviceType: 'Express Shipping',
    origin: 'New York, USA',
    destination: 'London, UK',
    amount: 245.50,
    status: 'Active',
    requestDate: '2026-02-05',
    validUntil: '2026-02-12',
  },
  {
    id: '2',
    quoteNumber: 'QT-2026-002',
    serviceType: 'Freight Shipping',
    origin: 'Los Angeles, USA',
    destination: 'Sydney, Australia',
    amount: 1850.00,
    status: 'Accepted',
    requestDate: '2026-02-01',
    validUntil: '2026-02-08',
  },
  {
    id: '3',
    quoteNumber: 'QT-2026-003',
    serviceType: 'Customs Clearance',
    origin: 'Miami, USA',
    destination: 'São Paulo, Brazil',
    amount: 320.75,
    status: 'Expired',
    requestDate: '2026-01-20',
    validUntil: '2026-01-27',
  },
];

export default function QuoteHistory() {
  const [quotes] = useState<Quote[]>(mockQuotes);
  const [filter, setFilter] = useState<string>('all');

  const filteredQuotes = filter === 'all' ? quotes : quotes.filter((q) => q.status.toLowerCase() === filter);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'accepted':
        return 'bg-primary/10 text-primary';
      case 'expired':
        return 'bg-muted text-muted-foreground';
      case 'declined':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Quote History ({filteredQuotes.length})
        </h2>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Quotes</option>
            <option value="active">Active</option>
            <option value="accepted">Accepted</option>
            <option value="expired">Expired</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
            <Icon name="PlusIcon" size={16} />
            New Quote
          </button>
        </div>
      </div>

      {/* Quotes Grid */}
      {filteredQuotes.length > 0 ? (
        <div className="grid gap-6">
          {filteredQuotes.map((quote) => (
            <div
              key={quote.id}
              className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{quote.quoteNumber}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{quote.serviceType}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-foreground">${quote.amount.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">USD</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="MapPinIcon" size={16} />
                    <span>Route</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {quote.origin} → {quote.destination}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="CalendarIcon" size={16} />
                    <span>Valid Until</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{quote.validUntil}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-border flex gap-3">
                {quote.status.toLowerCase() === 'active' && (
                  <>
                    <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                      <Icon name="CheckIcon" size={16} />
                      Accept Quote
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors">
                      <Icon name="DocumentDuplicateIcon" size={16} />
                      View Details
                    </button>
                  </>
                )}
                {quote.status.toLowerCase() === 'accepted' && (
                  <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                    <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={16} />
                    Create Shipment
                  </button>
                )}
                {quote.status.toLowerCase() === 'expired' && (
                  <button className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors">
                    <Icon name="ArrowPathIcon" size={16} />
                    Request New Quote
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl p-12 text-center border border-border">
          <Icon name="DocumentTextIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No quotes found</p>
        </div>
      )}
    </div>
  );
}